// src/services/adCampaignApi.ts

import api from './api.ts';

export type AdCampaignNameOrigin = 'meta' | 'manual' | 'none';

export interface AdCampaignIdentity {
  sourceId: string;
  displayName: string;
  manualAlias: string | null;
  metaAdName: string | null;
  metaCampaignId: string | null;
  metaCampaignName: string | null;
  nameOrigin: AdCampaignNameOrigin;
  canEditName: boolean;
}

// Interface representando as métricas retornadas pelo backend. Os campos de
// identidade são opcionais durante a transição para manter compatibilidade com
// respostas antigas do endpoint.
export interface AdCampaignMetrics {
  adId: number | null;
  sourceId: string;
  displayName?: string;
  manualAlias?: string | null;
  metaAdName?: string | null;
  metaCampaignId?: string | null;
  metaCampaignName?: string | null;
  nameOrigin?: AdCampaignNameOrigin;
  canEditName?: boolean;
  fileName: string | null;
  filePath: string | null;
  totalLeads: number;
  totalAgendamentos: number;
  totalComparecimentos: number;
  totalVendas: number;
  valorFaturado: number;
  valorPago: number;
  valorVendido: number;
  ticketMedio: number;
  investment: number;
  roas: number;
}

export interface AdCampaignHistoricalCpl {
  sourceCount: number;
  totalLeads: number;
  totalInvestment: number;
  cpl: number;
}

export interface MetaAdsSelectedAdAccount {
  id: string;
  name?: string;
  currency?: string;
}

export type MetaAdsConnectionState =
  | 'not_connected'
  | 'pending_account'
  | 'connected'
  | 'error'
  | 'disconnected';

export interface MetaAdsConnectionStatus {
  connected: boolean;
  status: MetaAdsConnectionState;
  metaUserId?: string;
  metaUserName?: string;
  tokenExpiresAt?: string | null;
  selectedAdAccount?: MetaAdsSelectedAdAccount | null;
  selectedAdAccounts?: MetaAdsSelectedAdAccount[];
  lastError?: string | null;
}

export interface MetaAdsOAuthUrlResponse {
  authorizationUrl: string;
  redirectUri: string;
  scopes: string[];
  permissionsSource?: 'business_login_configuration';
  forceRerequest?: boolean;
}

export interface MetaAdsAdAccount {
  id: string;
  account_id?: string;
  name?: string;
  account_status?: number;
  currency?: string;
}

export interface MetaAdsAdAccountsResponse {
  count: number;
  selectedAdAccountId?: string | null;
  selectedAdAccountIds?: string[];
  accounts: MetaAdsAdAccount[];
}

export interface MetaAdsPixel {
  id: string;
  name: string;
}

export interface MetaAdsPixelSelection {
  pixelId: string;
  pixelName: string;
  adAccountId: string;
}

export interface MetaAdsPixelDeliveryStatus {
  deliveryEnabled: false;
  deliveryAccess: 'blocked' | 'unverified';
}

export interface MetaAdsPixelsResponse extends MetaAdsPixelDeliveryStatus {
  adAccountId: string;
  pixels: MetaAdsPixel[];
  count: number;
  scopes: string[];
}

export interface MetaAdsPixelSelectionResponse extends MetaAdsPixelDeliveryStatus {
  selection: MetaAdsPixelSelection | null;
  status: 'not_selected' | 'reselection_required' | 'access_unavailable' | 'readable';
  scopes: string[];
}

export async function getMetaAdsPixels(
  clinicId: number,
  adAccountId: string,
  signal?: AbortSignal,
): Promise<MetaAdsPixelsResponse> {
  const params = new URLSearchParams({ clinic_id: String(clinicId), ad_account_id: adAccountId });
  const response = await api.get<MetaAdsPixelsResponse>(
    `/api/integrations/meta-ads/pixels?${params.toString()}`, { signal },
  );
  return response.data;
}

export async function getMetaAdsPixelSelection(
  clinicId: number,
  signal?: AbortSignal,
): Promise<MetaAdsPixelSelectionResponse> {
  const params = new URLSearchParams({ clinic_id: String(clinicId) });
  const response = await api.get<MetaAdsPixelSelectionResponse>(
    `/api/integrations/meta-ads/pixels/selection?${params.toString()}`, { signal },
  );
  return response.data;
}

export async function selectMetaAdsPixel(
  clinicId: number,
  adAccountId: string,
  pixelId: string,
): Promise<MetaAdsPixelDeliveryStatus & { selection: MetaAdsPixelSelection }> {
  const response = await api.post<MetaAdsPixelDeliveryStatus & { selection: MetaAdsPixelSelection }>(
    '/api/integrations/meta-ads/pixels/select',
    { clinic_id: clinicId, ad_account_id: adAccountId, pixel_id: pixelId },
  );
  return response.data;
}

export async function deleteMetaAdsPixelSelection(
  clinicId: number,
): Promise<MetaAdsPixelDeliveryStatus & { selection: null; status: 'not_selected' }> {
  const params = new URLSearchParams({ clinic_id: String(clinicId) });
  const response = await api.delete<MetaAdsPixelDeliveryStatus & { selection: null; status: 'not_selected' }>(
    `/api/integrations/meta-ads/pixels/selection?${params.toString()}`,
  );
  return response.data;
}

/**
 * 1) Retorna as métricas de anúncios (GET).
 *    /metrics/ad_campaigns?clinic_id=...&start_date=...&end_date=...
 */
export async function getAdCampaignMetrics(
  clinicId: number,
  startDate?: string,
  endDate?: string
): Promise<AdCampaignMetrics[]> {
  const params = new URLSearchParams();
  params.append('clinic_id', String(clinicId));
  if (startDate) params.append('start_date', startDate);
  if (endDate)   params.append('end_date', endDate);

  const url = `/metrics/ad_campaigns?${params.toString()}`;
  try {
    const resp = await api.get<AdCampaignMetrics[]>(url);
    return resp.data;
  } catch (error) {
    console.error('[adCampaignApi] Erro ao obter métricas:', error);
    throw error;
  }
}

export async function getAdCampaignHistoricalCpl(
  clinicId: number
): Promise<AdCampaignHistoricalCpl> {
  const params = new URLSearchParams();
  params.append('clinic_id', String(clinicId));

  const url = `/metrics/ad_campaigns/historical_cpl?${params.toString()}`;
  try {
    const resp = await api.get<AdCampaignHistoricalCpl>(url);
    return resp.data;
  } catch (error) {
    console.error('[adCampaignApi] Erro ao obter CPL histórico:', error);
    throw error;
  }
}

/**
 * 2) Obtém a imagem do anúncio (blob).
 *    /metrics/ad_campaigns/media/{ad_id}
 */
export async function getAdCampaignMediaBlob(adId: number): Promise<Blob> {
  try {
    const resp = await api.get(`/metrics/ad_campaigns/media/${adId}`, {
      responseType: 'blob',
    });
    return resp.data;
  } catch (error) {
    console.error('[adCampaignApi] Erro ao obter blob da imagem:', error);
    throw error;
  }
}

/**
 * Helper para converter Blob -> URL local (blob:).
 */
export async function getAdCampaignMediaUrl(adId: number): Promise<string> {
  const blob = await getAdCampaignMediaBlob(adId);
  return URL.createObjectURL(blob);
}

/**
 * 3) Cria/Atualiza investimento (mensal) de um anúncio, usando Body JSON:
 *    POST /metrics/ad_campaigns/investment
 *    { clinic_id, source_id, investment, reference_month }
 */
export interface AdCampaignInvestmentPayload {
  clinic_id: number;
  source_id: string;
  investment: number;
  reference_month: string; // "YYYY-MM-DD"
}

export async function setAdCampaignInvestmentMonthly(
  payload: AdCampaignInvestmentPayload
): Promise<void> {
  try {
    // Exemplo de body JSON
    // {
    //   "clinic_id": 1,
    //   "source_id": "1234",
    //   "investment": 500,
    //   "reference_month": "2023-07-01"
    // }
    const resp = await api.post('/metrics/ad_campaigns/investment', payload);
    console.log('[setAdCampaignInvestmentMonthly] Resposta do servidor:', resp.data);
  } catch (error) {
    console.error('[setAdCampaignInvestmentMonthly] Erro ao atualizar investimento:', error);
    throw error;
  }
}

export async function updateAdCampaignAlias(
  clinicId: number,
  sourceId: string,
  alias: string | null,
): Promise<AdCampaignIdentity> {
  try {
    const resp = await api.patch<AdCampaignIdentity>(
      `/metrics/ad_campaigns/${encodeURIComponent(sourceId)}/alias`,
      {
        clinic_id: clinicId,
        alias,
      },
    );
    return resp.data;
  } catch (error) {
    console.error('[adCampaignApi] Erro ao atualizar nome do anúncio:', error);
    throw error;
  }
}

export async function getMetaAdsConnection(
  clinicId: number
): Promise<MetaAdsConnectionStatus> {
  const params = new URLSearchParams();
  params.append('clinic_id', String(clinicId));

  try {
    const resp = await api.get<MetaAdsConnectionStatus>(`/api/integrations/meta-ads/connection?${params.toString()}`);
    return resp.data;
  } catch (error) {
    console.error('[adCampaignApi] Erro ao obter conexão Meta Ads:', error);
    throw error;
  }
}

export async function getMetaAdsOAuthUrl(
  clinicId: number,
  forceRerequest = false
): Promise<MetaAdsOAuthUrlResponse> {
  const params = new URLSearchParams();
  params.append('clinic_id', String(clinicId));
  if (forceRerequest) params.append('force_rerequest', 'true');

  try {
    const resp = await api.get<MetaAdsOAuthUrlResponse>(`/api/integrations/meta-ads/oauth/url?${params.toString()}`);
    return resp.data;
  } catch (error) {
    console.error('[adCampaignApi] Erro ao gerar URL OAuth Meta Ads:', error);
    throw error;
  }
}

export async function getMetaAdsAdAccounts(
  clinicId: number
): Promise<MetaAdsAdAccountsResponse> {
  const params = new URLSearchParams();
  params.append('clinic_id', String(clinicId));

  try {
    const resp = await api.get<MetaAdsAdAccountsResponse>(`/api/integrations/meta-ads/adaccounts?${params.toString()}`);
    return resp.data;
  } catch (error) {
    console.error('[adCampaignApi] Erro ao listar contas Meta Ads:', error);
    throw error;
  }
}

export async function selectMetaAdsAdAccount(
  clinicId: number,
  adAccountId: string
): Promise<{ message: string; connection: MetaAdsConnectionStatus }> {
  return selectMetaAdsAdAccounts(clinicId, [adAccountId]);
}

export async function selectMetaAdsAdAccounts(
  clinicId: number,
  adAccountIds: string[]
): Promise<{ message: string; connection: MetaAdsConnectionStatus }> {
  try {
    const resp = await api.post<{ message: string; connection: MetaAdsConnectionStatus }>(
      '/api/integrations/meta-ads/adaccounts/select',
      {
        clinic_id: clinicId,
        ad_account_ids: adAccountIds,
      }
    );
    return resp.data;
  } catch (error) {
    console.error('[adCampaignApi] Erro ao selecionar contas Meta Ads:', error);
    throw error;
  }
}

export async function disconnectMetaAdsConnection(clinicId: number): Promise<void> {
  const params = new URLSearchParams();
  params.append('clinic_id', String(clinicId));

  try {
    await api.delete(`/api/integrations/meta-ads/connection?${params.toString()}`);
  } catch (error) {
    console.error('[adCampaignApi] Erro ao desconectar Meta Ads:', error);
    throw error;
  }
}
